import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {DuplicateIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useReducer} from 'react'

import {LABELS} from '../constants/labels'
import {useUnmarkIssueAsDuplicateMutation} from '../mutations/unmark-issue-as-duplicate-mutation'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {MarkedAsDuplicateEventNode} from './issue-viewer-page-query-types'
import {IssueLink} from './IssueLink'
import {TimelineRow} from './row/TimelineRow'

// RELAY-CLEANUP(MarkedAsDuplicateEvent): widens nullable fields so still-Relay parent data is assignable.
// Remove when the timeline pagination fragments and other parents leave Relay.
export type MarkedAsDuplicateEventFragment = RelayCompat<
  Pick<
    MarkedAsDuplicateEventNode,
    'actor' | 'createdAt' | 'canonical' | 'duplicate' | 'isCanonicalOfClosedDuplicate' | 'databaseId' | 'viewerCanUndo'
  >
>

type MarkedAsDuplicateEventProps = {
  queryRef: MarkedAsDuplicateEventFragment
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.RefObject<HTMLLIElement | null>
  currentIssueId: string
  repositoryId: string
  ownerLogin: string
}

// Keeps track of undo-triggered events across remounts in the same page session.
const undoTriggeredEventKeys = new Set<string>()

export function MarkedAsDuplicateEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
  currentIssueId,
  repositoryId,
  ownerLogin,
}: MarkedAsDuplicateEventProps): React.ReactElement {
  const {
    actor,
    createdAt,
    canonical,
    duplicate,
    isCanonicalOfClosedDuplicate: isCanonicalDuplicate,
    databaseId,
    viewerCanUndo,
  } = queryRef

  const {
    mutate: unmarkIssueAsDuplicate,
    isPending: isUndoPending,
    isSuccess: isUndoComplete,
  } = useUnmarkIssueAsDuplicateMutation({issueUrl})

  const canonicalId =
    canonical?.__typename === 'Issue' || canonical?.__typename === 'PullRequest' ? canonical.id : undefined
  const duplicateId =
    duplicate?.__typename === 'Issue' || duplicate?.__typename === 'PullRequest' ? duplicate.id : undefined

  const undoEventKey = `${issueUrl}:${databaseId}:${currentIssueId}`
  const hasTriggeredUndo = undoTriggeredEventKeys.has(undoEventKey)
  const [, forceUndoButtonRefresh] = useReducer(value => value + 1, 0)

  const unMarkAsDuplicate = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canonicalId || !duplicateId || hasTriggeredUndo) return

    undoTriggeredEventKeys.add(undoEventKey)
    event.currentTarget.disabled = true
    forceUndoButtonRefresh()

    // WARN: the input in on purpose inverted from the perspective of the current issue. This seems to be a bug in the API, but it is consistent with the behavior of the "Mark as duplicate" mutation.
    const input = {duplicateId: currentIssueId, canonicalId: duplicateId}
    unmarkIssueAsDuplicate({input})
  }

  const highlighted = String(databaseId) === highlightedEventId
  const isUndoDisabled = hasTriggeredUndo || isUndoPending || isUndoComplete

  if (!duplicate) return <></>

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={DuplicateIcon}
      fillRow={viewerCanUndo}
    >
      <TimelineRow.Main>
        {isCanonicalDuplicate ? (
          <>
            marked <IssueLink inline data={duplicate} targetRepositoryId={repositoryId} targetOwnerLogin={ownerLogin} />{' '}
            as a duplicate of this issue{' '}
          </>
        ) : (
          <>
            {LABELS.timeline.markedAsDuplicate}
            <>
              &nbsp;
              <IssueLink inline data={duplicate} targetRepositoryId={repositoryId} targetOwnerLogin={ownerLogin} />{' '}
            </>
          </>
        )}
      </TimelineRow.Main>
      {viewerCanUndo &&
      (duplicate?.__typename === 'Issue' || duplicate?.__typename === 'PullRequest') &&
      duplicate.number &&
      !isCanonicalDuplicate ? (
        <TimelineRow.Trailing>
          <Button
            onClick={unMarkAsDuplicate}
            aria-label={LABELS.undoMarkIssueAsDuplicate(duplicate.number)}
            disabled={isUndoDisabled}
          >
            Undo
          </Button>
        </TimelineRow.Trailing>
      ) : null}
    </TimelineRow>
  )
}
